import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

// A shared host for Symphony's entity drill-ins (task / goal / agent-skills / file details). These
// used to open a right-docked side panel; they are all centered modals now (the Symphony DS "Modal /
// Drawer"). A drill-in renders a self-contained <Modal> — it owns its own scrim, close and Escape
// stack, reads `closeModal` from here, and deliberately does NOT touch the rail, so opening a detail
// never collapses the left navigation. One drill-in at a time; opening a new one replaces the current.
interface OpenModal {
  node: ReactNode;
  /** Optional owning-entity id, surfaced as `openTaskId` so its card can pin the DS Active state. */
  id: string | null;
}

interface TaskSidePanelValue {
  /** The rendered modal node, or null. */
  modal: ReactNode | null;
  /** The open drill-in's owning id, or null. */
  openTaskId: string | null;
  openModal: (node: ReactNode, id?: string) => void;
  closeModal: () => void;
}

const Context = createContext<TaskSidePanelValue | null>(null);

export function TaskSidePanelProvider({ children }: { children: ReactNode }) {
  const [modal, setModal] = useState<OpenModal | null>(null);
  const openModal = useCallback((node: ReactNode, id?: string) => setModal({ node, id: id ?? null }), []);
  const closeModal = useCallback(() => setModal(null), []);
  const value = useMemo(
    () => ({ modal: modal?.node ?? null, openTaskId: modal?.id ?? null, openModal, closeModal }),
    [modal, openModal, closeModal],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

// Tolerates a missing provider (no-op) so a stray mount never throws.
export function useTaskSidePanel(): TaskSidePanelValue {
  return useContext(Context) ?? { modal: null, openTaskId: null, openModal: () => {}, closeModal: () => {} };
}
